import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@/types/supabase';
import { useAuth } from '@/components/auth/AuthContext';
import { createHubspotContact } from '@/services/hubspotService';

// Create a Supabase client with the service role key for admin operations
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SERVICE_ROLE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

// Initialize service role client for admin operations
let serviceRoleClient: SupabaseClient<Database>;
try {
  serviceRoleClient = createClient<Database>(
    SUPABASE_URL,
    SERVICE_ROLE_KEY || ''
  );
} catch (err) {
  // console.error("Failed to initialize service role client:", err);
  // Fallback to regular supabase client
  serviceRoleClient = supabase;
}

// Function to update user information in Hubspot
const updateHubspotContact = async (userId: string, firstName: string, email: string, company: string = '') => {
  try {
    // Call our hubspot service instead of direct API call
    const response = await createHubspotContact(email, firstName, undefined, company, userId);
    
    if (!response.success) {
      return null;
    }
    
    // Store the Hubspot contact ID in the user's profile
    if (response.data.id && userId) {
      try {
        const { error: updateError } = await serviceRoleClient
          .from('profiles')
          .update({ 
            hubspot_contact_id: response.data.id,
            updated_at: new Date().toISOString() 
          })
          .eq('id', userId);
      } catch (updateError) {
        // Silently continue
      }
    }
    
    return response.data;
  } catch (error) {
    return null;
  }
};

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Function to ensure the Basic plan is assigned to Google/OAuth users
  const ensureBasicPlanAssigned = async (userId: string, userData: any) => {
    // console.log('[AUTH-CALLBACK] 🔍 Starting ensureBasicPlanAssigned for user:', userId);
    // console.log('[AUTH-CALLBACK] 📋 User metadata:', userData.user_metadata);
    // console.log('[AUTH-CALLBACK] 📋 App metadata:', userData.app_metadata);
    
    try {
      // Check if user already has a subscription
      // console.log('[AUTH-CALLBACK] 🔍 Checking for existing subscriptions');
      const { data: existingSubscriptions, error: checkError } = await serviceRoleClient
        .from('subscriptions')
        .select('id, plan_id, organization_id')
        .eq('user_id', userId);
      
      if (checkError) {
        // console.error('[AUTH-CALLBACK] ❌ Error checking existing subscriptions:', checkError);
        return;
      }

      // console.log('[AUTH-CALLBACK] 📋 Existing subscriptions:', existingSubscriptions);
      
      if (existingSubscriptions && existingSubscriptions.length > 0) {
        // console.log('[AUTH-CALLBACK] ℹ️ User already has subscription(s), skipping plan assignment');
        return;
      }

      // Get the Basic plan using UUID
      // console.log('[AUTH-CALLBACK] 🔍 Fetching Basic plan details');
      const { data: basicPlan, error: basicPlanError } = await serviceRoleClient
        .from('plans')
        .select('id, credits, name, stripe_price_id')
        .eq('id', '55b573b5-15cd-4c9d-a484-b7eb18403455')
        .single();
        
      if (basicPlanError) {
        // console.error('[AUTH-CALLBACK] ❌ Error fetching Basic plan:', basicPlanError);
        return;
      }
      
      if (!basicPlan) {
        // console.error('[AUTH-CALLBACK] ❌ No Basic plan found');
        return;
      }

      // console.log('[AUTH-CALLBACK] 📋 Basic plan:', basicPlan);

      // Get user's organization
      // console.log('[AUTH-CALLBACK] 🔍 Getting user profile');
      const { data: profile, error: profileError } = await serviceRoleClient
        .from('profiles')
        .select('organization_id, first_name, last_name')
        .eq('id', userId)
        .single();

      if (profileError) {
        // console.error('[AUTH-CALLBACK] ❌ Error getting profile:', profileError);
        return;
      }
      
      if (!profile?.organization_id) {
        // console.error('[AUTH-CALLBACK] ❌ Profile has no organization ID');
        return;
      }

      // console.log('[AUTH-CALLBACK] 📋 User profile:', profile);
      
      // Create a subscription record for the basic plan
      // console.log('[AUTH-CALLBACK] 🔧 Creating subscription record');
      const { data: subscriptionData, error: subscriptionError } = await serviceRoleClient
        .from('subscriptions')
        .insert([{
          user_id: userId,
          organization_id: profile.organization_id,
          plan_id: basicPlan.id,
          status: 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          current_period_start: new Date().toISOString(),
          current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days
        }])
        .select()
        .single();
      
      if (subscriptionError) {
        // console.error('[AUTH-CALLBACK] ❌ Error creating subscription:', subscriptionError);
        return;
      }

      // console.log('[AUTH-CALLBACK] ✅ Subscription created:', subscriptionData);

      // Check if the organization already has credits before attempting to add
      // console.log('[AUTH-CALLBACK] 🔍 Checking if credits record exists');
      const { data: existingCredits, error: creditsCheckError } = await serviceRoleClient
        .from('credits')
        .select('id, remaining_credits')
        .eq('organization_id', profile.organization_id);
        
      if (creditsCheckError) {
        // console.error('[AUTH-CALLBACK] ❌ Error checking existing credits:', creditsCheckError);
      } else {
        // console.log('[AUTH-CALLBACK] 📋 Existing credits records:', existingCredits);
        
        // Try to use the RPC method first (preferred method)
        try {
          // console.log('[AUTH-CALLBACK] 🔧 Adding credits via RPC');
          const { data: rpcData, error: creditsError } = await serviceRoleClient.rpc('add_organization_credits', {
            p_organization_id: profile.organization_id,
            p_amount: basicPlan.credits,
            p_description: `Initial ${basicPlan.name} plan credits`,
            p_transaction_type: 'subscription'
          });
          
          if (creditsError) {
            // console.error('[AUTH-CALLBACK] ❌ Error adding credits via RPC:', creditsError);
            throw creditsError;
          }
          
          // console.log('[AUTH-CALLBACK] ✅ Credits added via RPC:', rpcData);
        } catch (rpcError) {
          // console.warn('[AUTH-CALLBACK] ⚠️ Error with add_organization_credits RPC, falling back to direct insert:', rpcError);
          
          // If credits table already has an entry, update it
          if (existingCredits && existingCredits.length > 0) {
            // console.log('[AUTH-CALLBACK] 🔧 Updating existing credits record');
            const { data: updateData, error: updateError } = await serviceRoleClient
              .from('credits')
              .update({ 
                remaining_credits: existingCredits[0].remaining_credits + basicPlan.credits,
                updated_at: new Date().toISOString() 
              })
              .eq('organization_id', profile.organization_id)
              .select();
              
            if (updateError) {
              // console.error('[AUTH-CALLBACK] ❌ Error updating credits record:', updateError);
            } else {
              // console.log('[AUTH-CALLBACK] ✅ Credits record updated:', updateData);
            }
          } else {
            // Insert a new credits record if one doesn't exist
            // console.log('[AUTH-CALLBACK] 🔧 Creating new credits record');
            const { data: insertData, error: insertError } = await serviceRoleClient
              .from('credits')
              .insert([{ 
                organization_id: profile.organization_id,
                remaining_credits: basicPlan.credits,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString() 
              }])
              .select();
              
            if (insertError) {
              // console.error('[AUTH-CALLBACK] ❌ Error creating credits record:', insertError);
            } else {
              // console.log('[AUTH-CALLBACK] ✅ Credits record created:', insertData);
            }
          }
          
          // Also add to the credit history for consistency
          // console.log('[AUTH-CALLBACK] 🔧 Adding credit history record');
          const { data: historyData, error: historyError } = await serviceRoleClient
            .from('organization_credit_history')
            .insert([{
              organization_id: profile.organization_id,
              user_id: userId,
              amount: basicPlan.credits,
              balance: basicPlan.credits, // This is approximate
              description: `Initial ${basicPlan.name} plan credits`,
              transaction_type: 'subscription',
              created_at: new Date().toISOString()
            }])
            .select();
            
          if (historyError) {
            // console.error('[AUTH-CALLBACK] ❌ Error creating credit history record:', historyError);
          } else {
            // console.log('[AUTH-CALLBACK] ✅ Credit history record created:', historyData);
          }
        }
      }

      // After the profile is created or found and the plan is assigned, update Hubspot
      try {
        // Extract contact information for Hubspot
        const firstName = profile?.first_name || userData.user_metadata?.full_name?.split(' ')[0] || '';
        const email = userData.email;
        const company = '';  // Default empty company as it's not in profile response
        
        // Update the contact in Hubspot
        await updateHubspotContact(userId, firstName, email, company);
      } catch (hubspotError) {
        // Continue even if Hubspot update fails
      }

      // Final check of credit records for verification
      // console.log('[AUTH-CALLBACK] 🔍 Final check of credits');
      const { data: finalCredits, error: finalCreditsError } = await serviceRoleClient
        .from('credits')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .single();
        
      if (finalCreditsError) {
        // console.error('[AUTH-CALLBACK] ❌ Error in final credits check:', finalCreditsError);
      } else {
        // console.log('[AUTH-CALLBACK] 📋 Final credits record:', finalCredits);
      }
      
      // console.log('[AUTH-CALLBACK] 🔍 Final check of credit history');
      const { data: creditHistory, error: historyError } = await serviceRoleClient
        .from('organization_credit_history')
        .select('*')
        .eq('organization_id', profile.organization_id);
        
      if (historyError) {
        // console.error('[AUTH-CALLBACK] ❌ Error checking credit history:', historyError);
      } else {
        // console.log('[AUTH-CALLBACK] 📋 Credit history records:', creditHistory);
      }

      // console.log('[AUTH-CALLBACK] ✅ Successfully assigned Basic plan and credits to OAuth user:', userId);
    } catch (error) {
      // console.error('[AUTH-CALLBACK] 🚨 Error assigning Basic plan to OAuth user:', error);
    }
  };

  useEffect(() => {
    const handleCallback = async () => {
      // console.log('[AUTH-CALLBACK] 🚀 Auth callback page mounted');
      try {
        // Check if this is a password reset flow
        const type = searchParams.get('type');
        if (type === 'recovery') {
          // console.log('[AUTH-CALLBACK] 🔑 Password reset flow detected');
          navigate('/auth/reset-password');
          return;
        }

        // Add detailed logging for debugging authentication issues
        // console.log('[AUTH-CALLBACK] 📌 Auth parameters:', Object.fromEntries(searchParams.entries()));
        
        // console.log('[AUTH-CALLBACK] 🔍 Getting session');
        const sessionResult = await supabase.auth.getSession();
        
        // Log the full session result for debugging
        // console.log('[AUTH-CALLBACK] 📋 Session result:', JSON.stringify({
        //   data: sessionResult.data,
        //   error: sessionResult.error,
        //   status: sessionResult.error?.status,
        //   message: sessionResult.error?.message
        // }));
        
        const { data: { session }, error } = sessionResult;
        
        if (error) {
          // console.error('[AUTH-CALLBACK] ❌ Auth callback error:', error);
          toast.error(`Authentication failed: ${error.message || 'Unknown error'}`);
          navigate('/');
          return;
        }

        if (session) {
          // console.log('[AUTH-CALLBACK] ✅ Session found:', {
          //   userId: session.user.id,
          //   email: session.user.email,
          //   provider: session.user.app_metadata.provider
          // });
          
          // Set the user ID
          setUserId(session.user.id);

          // Check for profile and organization first
          // console.log('[AUTH-CALLBACK] 🔍 Checking user profile');
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('organization_id, first_name, last_name')
            .eq('id', session.user.id)
            .single();

          if (profileError && profileError.code !== 'PGRST116') {
            // console.error('[AUTH-CALLBACK] ❌ Error fetching profile:', profileError);
            toast.error('Error loading profile');
            navigate('/');
            return;
          }

          let organizationId: string | null = null;
          let needsOrgNameDialog = false;

          // If profile doesn't exist or has no organization_id, create it
          if (!profile || !profile.organization_id) {
            // console.log('[AUTH-CALLBACK] 🔧 Profile missing or has no organization, creating/updating profile and organization');
            
            try {
              // Create organization with empty name first
              // console.log('[AUTH-CALLBACK] 🔧 Creating new organization with empty name');
              const { data: newOrg, error: orgError } = await serviceRoleClient
                .from('organizations')
                .insert([{
                  name: '', // Empty name so dialog will prompt
                  owner_id: session.user.id,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                }])
                .select()
                .single();

              if (orgError) {
                // console.error('[AUTH-CALLBACK] ❌ Error creating organization:', orgError);
                toast.error('Error setting up your account');
                navigate('/');
                return;
              }

              // console.log('[AUTH-CALLBACK] ✅ Organization created with empty name:', newOrg);
              organizationId = newOrg.id;
              needsOrgNameDialog = true;

              // Extract user metadata
              const metadata = session.user.user_metadata || {};
              const first_name = metadata.first_name || 
                metadata.firstName || 
                metadata.name?.split(' ')[0] || 
                metadata.full_name?.split(' ')[0] || '';
              
              const last_name = metadata.last_name || 
                metadata.lastName || 
                metadata.name?.split(' ').slice(1).join(' ') || 
                metadata.full_name?.split(' ').slice(1).join(' ') || '';
              
              const avatar_url = metadata.avatar_url || metadata.picture || '';

              if (!profile) {
                // Create profile if it doesn't exist
                // console.log('[AUTH-CALLBACK] 🔧 Creating new profile');
                const { data: newProfile, error: profileCreateError } = await serviceRoleClient
                  .from('profiles')
                  .insert([{
                    id: session.user.id,
                    first_name,
                    last_name,
                    avatar_url,
                    organization_id: newOrg.id,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                  }])
                  .select()
                  .single();

                if (profileCreateError) {
                  // console.error('[AUTH-CALLBACK] ❌ Error creating profile:', profileCreateError);
                  toast.error('Error setting up your profile');
                  navigate('/');
                  return;
                }
                
                // console.log('[AUTH-CALLBACK] ✅ Profile created:', newProfile);
              } else {
                // Update existing profile with organization_id
                // console.log('[AUTH-CALLBACK] 🔧 Updating profile with organization ID');
                const { data: updatedProfile, error: profileUpdateError } = await serviceRoleClient
                  .from('profiles')
                  .update({
                    organization_id: newOrg.id,
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', session.user.id)
                  .select()
                  .single();

                if (profileUpdateError) {
                  // console.error('[AUTH-CALLBACK] ❌ Error updating profile with organization:', profileUpdateError);
                  toast.error('Error updating your profile');
                  navigate('/');
                  return;
                }
                
                // console.log('[AUTH-CALLBACK] ✅ Profile updated with organization ID:', updatedProfile);
              }
            } catch (setupError) {
              // console.error('[AUTH-CALLBACK] 🚨 Error in profile/organization setup:', setupError);
              toast.error('Unexpected error during account setup');
              navigate('/');
              return;
            }
          } else {
            // console.log('[AUTH-CALLBACK] ✅ Profile found with organization ID:', profile.organization_id);
            organizationId = profile.organization_id;
            
            // Check if organization name is empty
            const { data: org, error: orgError } = await supabase
              .from('organizations')
              .select('name')
              .eq('id', profile.organization_id)
              .single();

            if (orgError) {
              // console.error('[AUTH-CALLBACK] ❌ Error fetching organization:', orgError);
              needsOrgNameDialog = true; // Show dialog if we can't verify the name
            } else {
              // console.log('[AUTH-CALLBACK] 📋 Organization:', org);
              if (!org?.name || !org.name.trim()) {
                needsOrgNameDialog = true;
              }
            }
          }

          // For OAuth users (Google/Microsoft), we no longer auto-assign plans
          // Instead, they'll go through the same onboarding flow as email users
          const provider = session.user?.app_metadata?.provider;
          // console.log('[AUTH-CALLBACK] 📋 Auth provider:', provider);
          
          // Remove the automatic plan assignment for social logins
          // if ((provider === 'google' || provider === 'azure' || provider === 'linkedin_oidc') && organizationId) {
          //   console.log('[AUTH-CALLBACK] 🔧 OAuth user detected, ensuring Basic plan is assigned');
          //   await ensureBasicPlanAssigned(session.user.id, session.user);
          //   
          //   // Add a small delay to ensure all database operations are completed
          //   // This helps with race conditions in the credit service
          //   console.log('[AUTH-CALLBACK] ⏱️ Adding delay to ensure database consistency');
          //   await new Promise(resolve => setTimeout(resolve, 1500));
          // } else {
          //   console.log('[AUTH-CALLBACK] ℹ️ Non-OAuth user or missing organization ID, skipping plan check');
          // }
          
          // All users (both OAuth and email) now follow the same process
          // console.log('[AUTH-CALLBACK] ℹ️ Directing user to onboarding for role selection');

          // If organization name is empty, show the dialog
          if (needsOrgNameDialog) {
            // console.log('[AUTH-CALLBACK] 🔍 Organization has empty name:', {
            //   organizationId,
            //   needsOnboarding: needsOrgNameDialog,
            //   redirectingTo: needsOrgNameDialog ? '/onboarding' : '/dashboard'
            // });
            // Simply continue the flow, we don't need to show the dialog now
          } else {
            // console.log('[AUTH-CALLBACK] 🔍 Organization has a name, redirecting to dashboard');
          }

          // Check credits and subscription one more time for debugging
          // console.log('[AUTH-CALLBACK] 🔍 Final check - User subscriptions');
          const { data: subscriptions, error: subError } = await supabase
            .from('subscriptions')
            .select('*')
            .eq('user_id', session.user.id);
            
          if (subError) {
            // console.error('[AUTH-CALLBACK] ❌ Error checking subscriptions:', subError);
          } else {
            // console.log('[AUTH-CALLBACK] 📋 User subscriptions:', subscriptions);
          }
          
          if (organizationId) {
            // console.log('[AUTH-CALLBACK] 🔍 Final check - Organization credits');
            const { data: credits, error: creditsError } = await supabase
              .from('credits')
              .select('*')
              .eq('organization_id', organizationId);
              
            if (creditsError) {
              // console.error('[AUTH-CALLBACK] ❌ Error checking credits:', creditsError);
            } else {
              // console.log('[AUTH-CALLBACK] 📋 Organization credits:', credits);
            }
          }

          // Add another delay before redirect to ensure all DB operations complete
          await new Promise(resolve => setTimeout(resolve, 500));

          // Default path - success and redirect
          // console.log('[AUTH-CALLBACK] ✅ Auth flow complete, ready to navigate');
          toast.success('Successfully signed in!');
          
          // Check if the user needs to complete onboarding (has no organization name)
          if (organizationId && needsOrgNameDialog) {
            // console.log('[AUTH-CALLBACK] 🚀 Redirecting to onboarding page - organization name is empty');
            navigate('/onboarding');
          } else {
            // console.log('[AUTH-CALLBACK] 🚀 Redirecting to dashboard - organization has a name');
            navigate('/dashboard');
          }
        } else {
          // console.error('[AUTH-CALLBACK] ❌ No session found');
          toast.error('Authentication failed: No session found');
          navigate('/');
        }
      } catch (error) {
        // Improved error handling with more details
        // console.error('[AUTH-CALLBACK] 🚨 Unexpected error in auth callback:', error);
        
        // Check for specific error types
        let errorMessage = 'An unexpected error occurred';
        if (error instanceof Error) {
          errorMessage = `Error: ${error.message}`;
          // console.error('[AUTH-CALLBACK] 📋 Error stack:', error.stack);
        }
        
        // Check if it's a network error
        if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
          errorMessage = 'Network connection error. Please check your internet connection.';
        }
        
        // Check if it's a resource not found error
        if (typeof error === 'object' && error !== null && 'code' in error) {
          const errorCode = (error as any).code;
          if (errorCode === 'ERR_FILE_NOT_FOUND') {
            errorMessage = 'Required resources could not be loaded. Try clearing your browser cache.';
          }
        }
        
        toast.error(errorMessage);
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    handleCallback();
  }, [navigate, searchParams]);

  return (
    <>
      {isLoading && (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-white to-[#F8F7FF]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-roundone-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="text-2xl font-semibold text-gray-900">Authenticating...</h2>
            <p className="mt-2 text-gray-600">Please wait while we complete the sign-in process.</p>
          </div>
        </div>
      )}
    </>
  );
}
